import React, { forwardRef } from 'react';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { type CustomContentProps, useSnackbar } from 'notistack';

import {
  ActionContainer,
  ContextualCloseButton,
  ContextualContainer,
  ContextualContent,
  ContextualDetail,
  ContextualTitle,
  GenericCloseButton,
  GenericContainer,
  GenericMessage,
  StyledCloseIcon,
  StyledVariantIcon,
} from './Snackbar.styles';

// Variant configuration mapping
// Using MUI theme path strings that resolve automatically via sx prop
const VARIANT_CONFIG = {
  success: {
    color: 'success.main',
    Icon: CheckCircleIcon,
  },
  error: {
    color: 'error.main',
    Icon: ErrorIcon,
  },
  warning: {
    color: 'warning.main',
    Icon: WarningIcon,
  },
  info: {
    color: 'info.main',
    Icon: InfoIcon,
  },
  default: {
    color: 'primary.main',
    Icon: undefined, // No icon for default variant
  },
} as const;

/**
 * Unified Snackbar component that automatically adapts to contextual data.
 *
 * - Without `data` prop: Renders generic notification (6px border, 24px icon)
 * - With `data` prop: Renders rich contextual notification (12px border, 40px icon, title + details)
 */
export const Snackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, variant = 'default', style, ...rest }, ref) => {
    const { closeSnackbar } = useSnackbar();

    // Check if contextual data was passed (now type-safe via CustomContentProps augmentation)
    const contextualData = rest.data;
    const isContextual = !!contextualData;

    // Extract action prop if provided (for custom buttons like "Reload", etc.)
    const action = rest.action;
    const actionContent = typeof action === 'function' ? action(id) : action;

    // Check if close button should be hidden
    const hideCloseButton = rest.hideCloseButton ?? false;

    // Use default variant if the variant is not in our config
    const effectiveVariant = variant && variant in VARIANT_CONFIG ? variant : 'default';
    const config = VARIANT_CONFIG[effectiveVariant];
    const { color, Icon } = config;

    // For contextual notifications
    if (isContextual) {
      const { title, details } = contextualData;
      const displayTitle = title || message;
      const detailsArray = Array.isArray(details) ? details : details ? [details] : [];

      return (
        <ContextualContainer ref={ref} style={style} borderColor={color}>
          {/* Icon (if defined for this variant) */}
          {Icon && <StyledVariantIcon component={Icon} iconSize={40} iconColor={color} />}

          {/* Content */}
          <ContextualContent>
            {/* Title */}
            <ContextualTitle hasDetails={detailsArray.length > 0}>{displayTitle}</ContextualTitle>

            {/* Details */}
            {detailsArray.map((detail, index) => (
              <ContextualDetail key={index}>{detail}</ContextualDetail>
            ))}
          </ContextualContent>

          {/* Action buttons (if provided) */}
          {actionContent && <ActionContainer>{actionContent}</ActionContainer>}

          {/* Close button (can be hidden via hideCloseButton prop) */}
          {!hideCloseButton && (
            <ContextualCloseButton size="small" onClick={() => closeSnackbar(id)}>
              <StyledCloseIcon component={CloseIcon} />
            </ContextualCloseButton>
          )}
        </ContextualContainer>
      );
    }

    // Generic layout
    return (
      <GenericContainer ref={ref} style={style} borderColor={color}>
        {/* Icon (if defined for this variant) */}
        {Icon && <StyledVariantIcon component={Icon} iconSize={24} iconColor={color} />}

        {/* Message */}
        <GenericMessage>{message}</GenericMessage>

        {/* Action buttons (if provided) */}
        {actionContent && <ActionContainer>{actionContent}</ActionContainer>}

        {/* Close button (can be hidden via hideCloseButton prop) */}
        {!hideCloseButton && (
          <GenericCloseButton size="small" onClick={() => closeSnackbar(id)}>
            <StyledCloseIcon component={CloseIcon} />
          </GenericCloseButton>
        )}
      </GenericContainer>
    );
  },
);

Snackbar.displayName = 'Snackbar';
